'use client'

import React, { useEffect, useState } from 'react'
import { useUploadQueue } from '@/features/photos/hooks/use-upload-queue'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { ScrollArea } from '../ui/scroll-area'
import { Separator } from '../ui/separator'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import Link from 'next/link'

import { useRegion } from '@/shared/hooks/use-region'
import { buildRegionAwareHref } from '@/shared/lib/utils'
import { 
  CloudUpload, 
  CloudOff, 
  RefreshCw, 
  Trash2, 
  FileImage, 
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  X
} from 'lucide-react'

export default function UploadQueueButton() {
  const { regionSlug } = useRegion()
  const {
    isProcessing,
    totalCount,
    pendingCount,
    uploadingCount,
    failedCount,
    duplicateCount,
    activeUpload,
    items,
    clearQueue,
    retryFailed,
    removeItem,
  } = useUploadQueue()



  const [popoverOpen, setPopoverOpen] = useState(false)
  const [isOnline, setIsOnline] = useState(true)


  // Track online/offline status
  useEffect(() => {
    if (typeof window === 'undefined') return
    setIsOnline(navigator.onLine)
    
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Determine icon & status
  const hasFailed = failedCount > 0
  const isSyncing = isProcessing && isOnline && (uploadingCount > 0 || pendingCount > 0)

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>

        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full hover:bg-accent hover:text-accent-foreground"
          aria-label="Upload queue"
        >
          {/* Status Icon */}
          {!isOnline ? (
            <CloudOff className="h-5 w-5 text-amber-500 animate-pulse" />
          ) : isSyncing ? (
            <RefreshCw className="h-5 w-5 text-green-600 animate-spin" />
          ) : hasFailed ? (
            <AlertTriangle className="h-5 w-5 text-destructive animate-bounce" />
          ) : (
            <CloudUpload className={`h-5 w-5 ${totalCount === 0 ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
          )}

          {/* Floating Badge Count */}
          {totalCount > 0 && (
            <Badge
              variant={hasFailed ? 'destructive' : 'default'}
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full p-0 text-[10px] font-bold border-2 border-background"
            >
              {totalCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0 mr-4 shadow-xl border border-border bg-popover text-popover-foreground rounded-lg" align="end">
        {/* Header */}
        <div className="p-4 flex items-center justify-between bg-muted/30 rounded-t-lg">
          <div>
            <h4 className="font-semibold text-sm leading-none flex items-center gap-2">
              Upload Queue
              {!isOnline && (
                <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-600/30 bg-amber-500/10">
                  Offline
                </Badge>
              )}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              {totalCount} photo(s) in queue {duplicateCount > 0 && `(${duplicateCount} duplicate)`}
            </p>
          </div>
          {isSyncing && (
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
          )}
        </div>
        <Separator />

        {/* Scrollable list of items */}
        <ScrollArea className="h-64 p-2">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8 text-center text-muted-foreground text-xs">
              <CheckCircle2 className="h-8 w-8 text-muted/40 mb-2" />
              All uploads completed!
            </div>
          ) : (
            <div className="space-y-2 p-1">
              {items.map((item) => {
                const isUploadingThis = activeUpload && activeUpload.id === item.id
                const isFailedThis = item.status === 'failed'
                const isDuplicateThis = item.status === 'duplicate'
                
                return (
                  <div 
                    key={item.id} 
                    className="p-2 border border-border/50 rounded-md bg-background hover:bg-muted/10 transition-colors text-xs"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileImage className="h-4 w-4 shrink-0 text-primary/70" />
                      <span className="font-medium truncate flex-1 leading-tight">
                        {item.metadata?.filename || 'Untitled'}
                      </span>
                      
                      {/* Status Badges */}
                      {isUploadingThis ? (
                        <Badge variant="secondary" className="text-[10px] px-1 bg-green-500/10 text-green-600 border-green-500/20">
                          Uploading
                        </Badge>
                      ) : isFailedThis ? (
                        <Badge variant="destructive" className="text-[10px] px-1">
                          Failed ({item.attempts}/5)
                        </Badge>
                      ) : isDuplicateThis ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-600 border-amber-500/30 flex items-center gap-1 font-medium">
                          <Copy className="h-3 w-3" /> Duplicate
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1 text-muted-foreground">
                          Pending
                        </Badge>
                      )}

                      {/* Individual Row Remove Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-0 shrink-0 ml-1"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (item.id) removeItem(item.id)
                        }}
                        title="Remove from queue"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Description metadata */}
                    {item.metadata?.homeownerName ? (
                      <div className="text-[10px] text-muted-foreground leading-none mb-1">
                        {item.metadata.homeownerName}
                      </div>
                    ) : item.type === 'base' && item.metadata?.componentName ? (
                      <div className="text-[10px] text-muted-foreground leading-none mb-1">
                        {item.metadata.componentName}
                      </div>
                    ) : null}

                    {/* Inline Duplicate Info Message & In-App Deep Link */}
                    {isDuplicateThis && (() => {
                      let inAppHref: string | null = null

                      const targetHomeownerId = item.metadata?.homeownerId || item.metadata?.homeId

                      if (item.metadata?.workOrderId) {
                        inAppHref = buildRegionAwareHref(regionSlug, `/work-orders/${item.metadata.workOrderId}?photoId=${item.metadata.existingPhotoId || ''}`)
                      } else if (targetHomeownerId) {
                        inAppHref = buildRegionAwareHref(regionSlug, `/homeowners/${targetHomeownerId}?tab=photos&photoId=${item.metadata.existingPhotoId || ''}`)
                      } else if (item.type === 'base') {
                        inAppHref = buildRegionAwareHref(regionSlug, `/base?photoId=${item.metadata.existingPhotoId || ''}`)
                      }

                      return (
                        <div className="mt-1.5 p-1.5 rounded bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-700 dark:text-amber-400 font-medium">
                          <div>Duplicate file already uploaded (upload skipped).</div>
                          {inAppHref && (
                            <Link
                              href={inAppHref}
                              onClick={() => setPopoverOpen(false)}
                              className="inline-flex items-center gap-1 mt-1 text-primary hover:underline font-semibold"
                            >
                              <ExternalLink className="h-3 w-3" /> View
                            </Link>
                          )}
                        </div>
                      )
                    })()}



                    {/* Progress Bar */}
                    {isUploadingThis && (
                      <div className="mt-2 space-y-1">
                        <Progress value={activeUpload.progress} className="h-1 bg-green-500/10" />
                        <div className="flex justify-between text-[9px] text-muted-foreground leading-none">
                          <span>Progress</span>
                          <span>{activeUpload.progress}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>


        {/* Footer Actions */}
        <Separator />
        <div className="p-3 bg-muted/20 flex gap-2 justify-end rounded-b-lg">
          <Button
            variant="ghost"
            size="sm"
            className="text-[11px] h-8 px-2.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => clearQueue()}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Clear Queue
          </Button>

          {hasFailed && (
            <Button
              variant="outline"
              size="sm"
              className="text-[11px] h-8 px-2.5"
              disabled={!isOnline}
              onClick={() => retryFailed()}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry Failed
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
